/** Neighborhood Adventure forms. Render-only: dimensions never feed collision or agents. */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { CreateSphereVertexData } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Matrix, Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import type { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
export type NeighborhoodRgb = readonly [number, number, number];
type MaterialFactory = (name: string, rgb: NeighborhoodRgb) => StandardMaterial;

/** Rounded cuboid with planar centres, preserving the specified outer envelope. */
export function neighborhoodRoundedBox(name: string, scene: Scene, rounding = 0.2): Mesh {
  const data = CreateSphereVertexData({ diameter: 1, segments: 12 });
  const positions = Array.from(data.positions!);
  // Map the sphere onto a cube, then round only the edges. This has flat broad
  // panels, unlike scaling a sphere, and smooth vertex normals at the bevel.
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i]!, y = positions[i + 1]!, z = positions[i + 2]!;
    const longest = Math.max(Math.abs(x), Math.abs(y), Math.abs(z), 1e-6);
    positions[i] = x / longest * (0.5 - rounding) + x * rounding * 2;
    positions[i + 1] = y / longest * (0.5 - rounding) + y * rounding * 2;
    positions[i + 2] = z / longest * (0.5 - rounding) + z * rounding * 2;
  }
  data.positions = positions;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, data.indices!, normals);
  data.normals = normals;
  const mesh = new Mesh(name, scene); data.applyToMesh(mesh); return mesh;
}

export function buildNeighborhoodRobot(scene: Scene, host: TransformNode, width: number, length: number, material: MaterialFactory, label: StandardMaterial) {
  const shell: Mesh[] = [];
  function part(name: string, size: readonly number[], pos: readonly number[], rgb: NeighborhoodRgb, parent: TransformNode = host): Mesh {
    const mesh = neighborhoodRoundedBox(name, scene);
    mesh.scaling.set(size[0]!, size[1]!, size[2]!); mesh.position.set(pos[0]!, pos[1]!, pos[2]!);
    mesh.material = material(name, rgb); mesh.parent = parent; return mesh;
  }
  const cream: NeighborhoodRgb = [0.94, 0.92, 0.84];
  shell.push(part('adventureCreamShell', [width * .92, .44, length * .92], [0, .37, 0], cream));
  shell.push(part('adventureLowerBumper', [width * .98, .12, length * .96], [0, .18, 0], [.23, .28, .29]));
  shell.push(part('adventureFace', [width * .74, .22, .055], [0, .43, length * .455], [.035, .075, .09]));
  for (const side of [-1, 1]) {
    const eye = part('adventureCyanEye', [.043, .079, .018], [side * width * .15, .45, length * .493], [.18, .83, .87]);
    eye.material = material('adventureEyeGlow', [.18, .83, .87]);
    (eye.material as StandardMaterial).emissiveColor.set(.06, .35, .4); shell.push(eye);
    for (const z of [-.29, .29]) {
      const wheel = CreateCylinder('adventureWheel', { diameter: .27, height: .075, tessellation: 20 }, scene);
      wheel.rotation.z = Math.PI / 2; wheel.position.set(side * width * .44, .145, z * length); wheel.material = material('adventureRubber', [.09, .12, .13]); wheel.parent = host; shell.push(wheel);
      const hub = CreateCylinder('adventureWheelHub', { diameter: .12, height: .08, tessellation: 16 }, scene);
      hub.rotation.z = Math.PI / 2; hub.position.copyFrom(wheel.position); hub.material = material('adventureHub', [.68, .73, .7]); hub.parent = host; shell.push(hub);
    }
  }
  const carrier = part('adventureFoodCarrier', [width * .79, .32, length * .57], [0, .76, -.045], [1, .34, .075]);
  part('adventureCarrierLid', [width * .82, .045, length * .6], [0, .48, 0], [1, .46, .12], carrier).scaling.divideInPlace(carrier.scaling);
  // Children below use the carrier's local coordinates (the carrier mesh is scaled).
  const rearLabel = CreateBox('adventureDeliveryLabel', { width: .70, height: .56, depth: .015 }, scene);
  rearLabel.parent = carrier; rearLabel.position.set(0, 0, -.51); rearLabel.material = label;
  const seam = CreateBox('adventureCarrierSeam', { width: 1.015, height: .024, depth: 1.015 }, scene);
  seam.parent = carrier; seam.position.y = .32; seam.material = material('adventureCarrierPiping', [1, .76, .4]);
  const handle = part('adventureHandle', [width * .30, .035, .055], [0, .94, -.045], [.22, .25, .24]);
  // Handle follows the animated package, and sits above its orange lid.
  handle.parent = carrier; handle.position.set(0, .55, 0); handle.scaling.divideInPlace(carrier.scaling);
  const armL = part('adventureArmL', [.07, .15, .13], [-.22, .54, -.01], cream);
  const armR = part('adventureArmR', [.07, .15, .13], [.22, .54, -.01], cream);
  shell.push(armL, armR);
  const tail = new TransformNode('adventureTailPivot', scene); tail.parent = host; tail.position.set(0, .29, -length * .44);
  const tailMesh = part('adventureTail', [.09, .10, .10], [0, 0, -.03], cream, tail); shell.push(tailMesh);
  return { shell, carrier, armL, armR, tail };
}

export type NeighborhoodBatch = (shape: 'box' | 'cyl' | 'sphere', color: NeighborhoodRgb, matrix: Matrix, texture?: string) => void;
/** A narrow shophouse module; all ground-level pieces remain inside its footprint.
 * Extra depth is restricted to balconies and awnings above pedestrian head height. */
export function neighborhoodHouse(add: NeighborhoodBatch, x: number, z: number, yaw: number, height: number, seed: number, night: boolean): void {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const place = (shape: 'box'|'cyl'|'sphere', color: NeighborhoodRgb, sx: number, sy: number, sz: number, px: number, py: number, pz: number, texture?: string) => {
    const matrix = Matrix.Compose(new Vector3(sx, sy, sz), Quaternion.RotationYawPitchRoll(yaw, 0, 0), new Vector3(x + c * px + s * pz, py, z - s * px + c * pz));
    add(shape, color, matrix, texture);
  };
  const plaster: NeighborhoodRgb[] = [[.92,.77,.55], [.76,.84,.73], [.93,.87,.73], [.77,.84,.85], [.88,.69,.55]];
  const tint = plaster[seed % plaster.length]!;
  const suffix = night ? '_night' : '';
  place('box', tint, 2.94, height - 2.8, 2.6, 0, 2.8 + (height - 2.8)/2, 0, `textures/facade${suffix}.jpg`);
  place('box', [.97,.94,.84], 2.94, 2.8, 2.6, 0, 1.4, 0, `textures/storefront${seed % 3 === 0 ? '' : seed % 3 === 1 ? '_b' : '_c'}${suffix}.jpg`);
  place('box', [.95,.88,.72], 3.02, .17, 2.75, 0, height + .04, 0);
  // Projecting striped awning, recessed shutters and doorway piers.
  const awning: NeighborhoodRgb = seed % 2 ? [.18,.43,.40] : [.79,.32,.18];
  for (let i = 0; i < 7; i++) place('box', i % 2 ? [.94,.87,.69] : awning, .42, .09, .74, -1.26 + i*.42, 2.73, 1.49);
  place('box', awning, 2.94, .19, .07, 0, 2.60, 1.84);
  for (const side of [-1,1]) place('box', [.89,.80,.65], .12, 2.8, .15, side * 1.41, 1.4, 1.33);
  for (let y = 3.1; y < height - 1.6; y += 2.6) {
    place('box', [.95,.88,.73], 2.50, .13, .62, 0, y, 1.51);
    // Open rails rather than opaque boxes: the window remains visible.
    place('box', [.22,.30,.29], 2.50, .055, .055, 0, y+.67, 1.78);
    for (let i = 0; i < 7; i++) place('box', [.26,.34,.31], .035, .65, .035, -1.2+i*.4, y+.34, 1.78);
    place('box', [.51,.31,.19], .55, .22, .25, .73, y+.15, 1.54);
    for (const dx of [-.15,.12]) place('sphere', [.22,.43,.24], .37, .35, .32, .73+dx, y+.4, 1.54);
  }
  // Visible water tank, AC compressor and cable bundle, outside the roadway.
  place('cyl', [.65,.73,.72], .65, .80, .65, -.65, height+.47, -.3);
  place('box', [.76,.79,.72], .62, .40, .24, 1.02, 3.6, 1.44);
  for (const y of [4.5,4.57,4.68]) place('box', [.13,.18,.18], 3.06, .018, .018, 0, y, 1.61);
}
